import { useEffect, useRef, useState } from "react";
import { Instagram, LogOut } from "lucide-react";
import type { InstagramPageChoice } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useChooseInstagramPage,
  useConnectInstagram,
  useDisconnectInstagram,
  useInstagramStatus,
} from "@/hooks/useInstagram";
import { loadFacebookSdk, type FacebookSdk } from "@/lib/facebook";
import { runWithPopupWatch } from "@/lib/popup-watch";

const APP_ID = import.meta.env.VITE_META_APP_ID as string | undefined;
// Its own Facebook Login for Business configuration: WhatsApp's Embedded Signup
// config grants WhatsApp assets, not a Page and its Instagram account.
const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID_INSTAGRAM as string | undefined;
// The SDK is loaded once per page (lib/facebook memoizes it), so it must match
// the version WabaEmbeddedSignup asks for.
const SDK_VERSION = "v23.0";

/**
 * Admin → Instagram: connect the client's Instagram professional account so the
 * agent answers its DMs (docs/INSTAGRAM_SETUP.md). Independent of the WhatsApp
 * connection — a Baileys client can have it too.
 */
export function InstagramConnect(): React.JSX.Element {
  const { data: status, isPending } = useInstagramStatus();

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-ink-100 px-6 py-5">
        <h1 className="text-lg font-semibold text-ink-900">Conexión Instagram</h1>
        <p className="mt-0.5 text-sm text-ink-500">
          Conecta la cuenta de Instagram para que el asistente responda sus mensajes directos.
        </p>
      </div>
      <div className="flex flex-1 flex-col items-center gap-6 overflow-y-auto p-6">
        {isPending || !status ? (
          <span className="text-sm text-ink-500">Cargando…</span>
        ) : !status.enabled ? (
          <Notice title="Instagram no está activo en el servidor">
            Pide a soporte que lo active para tu cuenta.
          </Notice>
        ) : !status.entitled ? (
          <Notice title="No incluido en tu plan">
            {status.connected
              ? `La cuenta @${status.username ?? ""} sigue conectada, pero el asistente no responde por Instagram mientras el plan no lo incluya.`
              : "Tu plan actual no incluye Instagram."}
          </Notice>
        ) : status.connected ? (
          <ConnectedCard username={status.username} connectedAt={status.connectedAt} />
        ) : (
          <ConnectCard />
        )}
      </div>
    </div>
  );
}

function Notice({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="w-full max-w-sm rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{children}</p>
    </div>
  );
}

function ConnectCard(): React.JSX.Element {
  const connect = useConnectInstagram();
  const choose = useChooseInstagramPage();
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<{ selectionId: string; pages: InstagramPageChoice[] } | null>(null);
  const sdkRef = useRef<FacebookSdk | null>(null);

  // Preloaded on mount, not on the click: FB.login must run inside the click's
  // own task, or the browser treats Meta's window as an unsolicited popup and
  // blocks it (the same rule WabaEmbeddedSignup follows).
  useEffect(() => {
    if (!APP_ID) return;
    let cancelled = false;
    void (async () => {
      const fb = await loadFacebookSdk(APP_ID, SDK_VERSION).catch(() => null);
      if (!cancelled) sdkRef.current = fb;
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function launch(): void {
    setError(null);
    if (!APP_ID || !CONFIG_ID) {
      setError("Falta la configuración de Meta (VITE_META_APP_ID / VITE_META_CONFIG_ID_INSTAGRAM).");
      return;
    }
    const fb = sdkRef.current;
    if (!fb) {
      setError("El SDK de Facebook todavía no carga. Espera unos segundos y vuelve a intentar.");
      return;
    }
    const outcome = runWithPopupWatch(() =>
      fb.login(
        (response) => {
          const code = response.authResponse?.code;
          if (!code) {
            setError("No se recibió la autorización de Meta.");
            return;
          }
          connect.mutate(code, {
            onSuccess: (result) => {
              if (result.status === "choose_page") setChoice(result);
            },
          });
        },
        { config_id: CONFIG_ID, response_type: "code", override_default_response_type: true },
      ),
    );
    if (outcome === "blocked") {
      setError("Tu navegador bloqueó la ventana de Meta. Permite las ventanas emergentes y vuelve a intentar.");
    }
  }

  if (choice) {
    return (
      <div className="flex w-full max-w-sm flex-col gap-3 rounded-xl border border-ink-100 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-ink-900">¿Qué cuenta de Instagram quieres conectar?</p>
        {choice.pages.map((page) => (
          <Button
            key={page.pageId}
            variant="outline"
            disabled={choose.isPending}
            onClick={() => choose.mutate({ selectionId: choice.selectionId, pageId: page.pageId })}
          >
            {page.username ? `@${page.username}` : page.pageName}
            <span className="ml-1 text-ink-400">· {page.pageName}</span>
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border border-ink-100 bg-white p-8 text-center shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-pink-50">
        <Instagram className="text-pink-600" size={26} />
      </div>
      <p className="text-sm text-ink-600">
        Inicia sesión con la cuenta de Facebook que administra la Página vinculada a tu Instagram
        profesional.
      </p>
      <Button onClick={launch} disabled={connect.isPending}>
        {connect.isPending ? "Conectando…" : "Conectar Instagram"}
      </Button>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function ConnectedCard({
  username,
  connectedAt,
}: {
  username: string | null;
  connectedAt: string | null;
}): React.JSX.Element {
  const disconnect = useDisconnectInstagram();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border border-ink-100 bg-white p-8 text-center shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-pink-50">
        <Instagram className="text-pink-600" size={26} />
      </div>
      <div>
        <p className="text-sm font-semibold text-ink-900">{username ? `@${username}` : "Cuenta conectada"}</p>
        {connectedAt && (
          <p className="mt-0.5 text-xs text-ink-500">
            Conectada desde {new Date(connectedAt).toLocaleDateString("es-MX")}
          </p>
        )}
      </div>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <LogOut size={14} className="mr-1.5" />
        Desconectar
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desconectar Instagram?</AlertDialogTitle>
            <AlertDialogDescription>
              El asistente dejará de responder los mensajes de Instagram. Las conversaciones
              existentes no se borran.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnect.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600"
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
